import React, {
  createContext,
  useState,
  useEffect,
  ReactNode,
  useContext,
  useCallback,
  useRef,
} from "react";
import apiClient from "../api/axios";
import {
  ExtractedContract,
  RentRollItem,
  NormalizedDataResponse,
  PaginatedDataResponse,
  PaginationInfo,
} from "../types/contract.d";
import { AuthContext } from "./AuthContext";

export interface AllDataContextType {
  // 既存のプロパティ（後方互換性のため維持）
  contracts: ExtractedContract[];
  rentRollItems: RentRollItem[];
  loading: boolean;
  error: string | null;
  fetchData: () => Promise<void>;
  forceRefresh: () => Promise<void>;
  setContracts: React.Dispatch<React.SetStateAction<ExtractedContract[]>>;

  // 新しいページネーション機能
  mode: "browse" | "search"; // 通常モード or 検索モード
  dataMode: "full" | "paginated" | null; // パスA or パスB
  searchQuery: string;
  pagination: PaginationInfo | null;
  hasNextPage: boolean;
  hasPrevPage: boolean;

  // ページネーション操作
  fetchNextPage: () => Promise<void>;
  fetchPrevPage: () => Promise<void>;
  searchData: (query: string) => Promise<void>;
  clearSearch: () => Promise<void>;
  goToFirstPage: () => Promise<void>;
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
  setItemsPerPage: (count: number) => Promise<void>;
  sortOrder: "asc" | "desc";
  setSortOrder: (order: "asc" | "desc") => void;
  hasInitialLoaded: boolean;
}

export const AllDataContext = createContext<AllDataContextType | undefined>(
  undefined,
);

interface AllDataProviderProps {
  children: ReactNode;
}

export const AllDataProvider: React.FC<AllDataProviderProps> = ({
  children,
}) => {
  // 既存の状態（後方互換性のため維持）
  const [contracts, setContracts] = useState<ExtractedContract[]>([]);
  const [rentRollItems, setRentRollItems] = useState<RentRollItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [hasInitialLoaded, setHasInitialLoaded] = useState<boolean>(false);

  // 新しいページネーション用の状態
  const STORAGE_KEY_ITEMS_PER_PAGE = "pagination_itemsPerPage";
  const DEFAULT_ITEMS_PER_PAGE = 50;

  const [itemsPerPage, setItemsPerPageState] = useState<number>(() => {
    // localStorageから読み込み、無効な値の場合はデフォルト値を使用
    const stored = localStorage.getItem(STORAGE_KEY_ITEMS_PER_PAGE);
    if (stored) {
      const parsed = parseInt(stored, 10);
      if ([10, 20, 50, 100].includes(parsed)) {
        return parsed;
      }
    }
    return DEFAULT_ITEMS_PER_PAGE;
  });

  const [mode, setMode] = useState<"browse" | "search">("browse");
  const [dataMode, setDataMode] = useState<"full" | "paginated" | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const paginationRef = useRef<PaginationInfo | null>(null);
  useEffect(() => {
    paginationRef.current = pagination;
  }, [pagination]);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [sortOrder, setSortOrderState] = useState<"asc" | "desc">("asc");

  // ページキャッシュ（カーソルをキーとしてページデータを保存）
  const [browseCache, setBrowseCache] = useState<
    Map<string, ExtractedContract[]>
  >(new Map());
  const [searchCache, setSearchCache] = useState<
    Map<string, ExtractedContract[]>
  >(new Map());

  // カーソル履歴（「前へ」ボタン用）
  const [browseCursorHistory, setBrowseCursorHistory] = useState<string[]>([]);
  const [searchCursorHistory, setSearchCursorHistory] = useState<string[]>([]);

  // companyIdの変更を検知してstateをクリア
  const authContext = useContext(AuthContext);
  const currentCompanyId = authContext?.companyId;

  useEffect(() => {
    // companyIdが変更された場合、全データをクリア
    setContracts([]);
    setRentRollItems([]);
    setError(null);
    setMode("browse");
    setDataMode(null);
    setSearchQuery("");
    setPagination(null);
    setBrowseCache(new Map());
    setSearchCache(new Map());
    setBrowseCursorHistory([]);
    setSearchCursorHistory([]);
    setHasInitialLoaded(false);
  }, [currentCompanyId]);

  const fetchDataInternal = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get<NormalizedDataResponse>(
        "/api/normalized-data/",
      );
      const sortedContracts = response.data.contracts.sort(
        (a, b) => (a.id ?? 0) - (b.id ?? 0),
      );

      setContracts(sortedContracts);
      setRentRollItems([]);
    } catch (err) {
      console.error("Failed to fetch normalized data:", err);
      setError("データの取得に失敗しました。");
    } finally {
      setLoading(false);
    }
  }, [setLoading, setError, setContracts, setRentRollItems]);

  // 新しいページネーション対応のデータ取得
  const fetchPaginatedDataInternal = useCallback(
    async (
      cursor: string | null = null,
      query: string = "",
      isSearch: boolean = false,
      order: string = "asc",
    ) => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        if (cursor) params.append("cursor", cursor);
        if (query) params.append("query", query);
        if (order) params.append("order", order);

        // Add limit for items per page
        params.append("limit", String(itemsPerPage));

        const response = await apiClient.get<PaginatedDataResponse>(
          `/api/normalized-data-paginated/?${params.toString()}`,
        );

        const {
          contracts: newContracts,
          pagination: newPagination,
          mode: newMode,
        } = response.data;

        setDataMode(newMode);
        setPagination(newPagination);

        // Calculate totalPages
        // Path A/BともにtotalCountが返ってくるのでtotalPagesを計算
        if (
          newPagination.totalCount !== undefined &&
          newPagination.totalCount !== null
        ) {
          setTotalPages(Math.ceil(newPagination.totalCount / itemsPerPage));
        } else {
          setTotalPages(1); // Default to 1 if totalCount is not available
        }

        // パスA（全件取得）の場合
        if (newMode === "full") {
          setContracts(newContracts);
          // キャッシュにも保存（関数型アップデートでキャッシュ依存を排除）
          if (isSearch) {
            setSearchCache((prev) => {
              const cache = new Map(prev);
              cache.set("all", newContracts);
              return cache;
            });
          } else {
            setBrowseCache((prev) => {
              const cache = new Map(prev);
              cache.set("all", newContracts);
              return cache;
            });
          }
        }
        // パスB（ページネーション）の場合
        // 最初のリクエスト（cursor=null）では5000件を取得してキャッシュ
        // 追加リクエスト（cursor有り）では既存データに追加
        else {
          if (!cursor) {
            // 最初のリクエスト: 5000件を取得してキャッシュ
            setContracts(newContracts);
            if (isSearch) {
              setSearchCache((prev) => {
                const cache = new Map(prev);
                cache.set("initial", newContracts);
                return cache;
              });
            } else {
              setBrowseCache((prev) => {
                const cache = new Map(prev);
                cache.set("initial", newContracts);
                return cache;
              });
            }
          } else {
            // 追加リクエスト: 既存データに追加
            setContracts((prev) => [...prev, ...newContracts]);
          }
        }

        setRentRollItems([]);
      } catch (err) {
        console.error("Failed to fetch paginated data:", err);
        setError("データの取得に失敗しました。");
      } finally {
        setLoading(false);
        setHasInitialLoaded(true);
      }
    },
    [itemsPerPage],
  );

  const fetchData = useCallback(async () => {
    // フルデータ取得を使用して dataMode を 'full' に設定
    setDataMode('full');
    await fetchDataInternal();
  }, [fetchDataInternal]);

  const forceRefresh = useCallback(async () => {
    // キャッシュをクリアして再取得
    setBrowseCache(new Map());
    setSearchCache(new Map());
    setBrowseCursorHistory([]);
    setSearchCursorHistory([]);
    await fetchPaginatedDataInternal(null, "", false, sortOrder);
  }, [fetchPaginatedDataInternal, sortOrder]);

  // ソート順変更関数
  const setSortOrder = useCallback(
    async (newOrder: "asc" | "desc") => {
      setSortOrderState(newOrder);
      setCurrentPage(1);

      // Path A（全件取得）の場合は、クライアント側でソートするだけで済む
      if (dataMode === "full") {
        // sortOrderState の変更により、ConfirmPage 側のuseEffectでソートが実行される
        return;
      }

      // Path B（ページネーション）の場合は、データを再取得
      // 既存データを即座にクリアして、古いデータが表示されないようにする
      setContracts([]);
      // キャッシュもクリア
      setBrowseCache(new Map());
      setSearchCache(new Map());
      setBrowseCursorHistory([]);
      setSearchCursorHistory([]);
      // 新しいソート順でデータを再取得
      const isSearch = mode === "search";
      const query = isSearch ? searchQuery : "";
      await fetchPaginatedDataInternal(null, query, isSearch, newOrder);
    },
    [mode, searchQuery, fetchPaginatedDataInternal, dataMode],
  );

  // 最初のページを取得
  const goToFirstPage = useCallback(async () => {
    const isSearch = mode === "search";
    const query = isSearch ? searchQuery : "";

    // カーソル履歴をクリア
    if (isSearch) {
      setSearchCursorHistory([]);
    } else {
      setBrowseCursorHistory([]);
    }
    setCurrentPage(1); // Set current page to 1
    await fetchPaginatedDataInternal(null, query, isSearch, sortOrder);
  }, [mode, searchQuery, fetchPaginatedDataInternal, sortOrder]);

  // ページサイズ変更
  const setItemsPerPage = useCallback(
    async (newCount: number) => {
      // バリデーション
      if (![10, 20, 50, 100].includes(newCount)) {
        console.error("Invalid itemsPerPage value:", newCount);
        return;
      }

      // localStorageに保存
      localStorage.setItem(STORAGE_KEY_ITEMS_PER_PAGE, newCount.toString());

      // 状態を更新
      setItemsPerPageState(newCount);

      // 最初のページに戻る
      setCurrentPage(1);

      // Path Bの場合のみキャッシュクリアとデータ再取得（Path Aは全件取得済みなので不要）
      if (dataMode === "paginated") {
        const isSearch = mode === "search";
        const query = isSearch ? searchQuery : "";

        // カーソル履歴をクリア
        if (isSearch) {
          setSearchCursorHistory([]);
        } else {
          setBrowseCursorHistory([]);
        }

        // キャッシュをクリア（limitが変わったので古いキャッシュは無効）
        setBrowseCache(new Map());
        setSearchCache(new Map());

        // 新しいitemsPerPageでデータを再取得
        await fetchPaginatedDataInternal(null, query, isSearch, sortOrder);
      }
    },
    [dataMode, mode, searchQuery, fetchPaginatedDataInternal, sortOrder],
  );

  // 次のページを取得
  const fetchNextPage = useCallback(async () => {
    // Use Ref to avoid stale closure in loops
    const currentPagination = paginationRef.current;
    if (!currentPagination?.hasMore || !currentPagination?.nextCursor) {
      return;
    }

    const isSearch = mode === "search";
    const query = isSearch ? searchQuery : "";
    const cache = isSearch ? searchCache : browseCache;
    const cacheKey = currentPagination.nextCursor;

    // キャッシュにあればそれを使用
    if (cache.has(cacheKey)) {
      const cachedContracts = cache.get(cacheKey)!;
      setContracts((prev) => [...prev, ...cachedContracts]);
      // カーソル履歴に現在のカーソルを追加
      if (isSearch) {
        setSearchCursorHistory((prev) => [
          ...prev,
          currentPagination.prevCursor || "first",
        ]);
      } else {
        setBrowseCursorHistory((prev) => [
          ...prev,
          currentPagination.prevCursor || "first",
        ]);
      }
      setCurrentPage((prev) => prev + 1); // Increment currentPage
      return;
    }

    // カーソル履歴に現在のカーソルを追加
    if (isSearch) {
      setSearchCursorHistory((prev) => [
        ...prev,
        currentPagination.prevCursor || "first",
      ]);
    } else {
      setBrowseCursorHistory((prev) => [
        ...prev,
        currentPagination.prevCursor || "first",
      ]);
    }
    setCurrentPage((prev) => prev + 1); // Increment currentPage
    await fetchPaginatedDataInternal(
      currentPagination.nextCursor,
      query,
      isSearch,
      sortOrder,
    );
  }, [
    mode,
    searchQuery,
    searchCache,
    browseCache,
    fetchPaginatedDataInternal,
    sortOrder,
  ]);

  // 前のページを取得（キャッシュから）
  const fetchPrevPage = useCallback(async () => {
    const isSearch = mode === "search";
    const cursorHistory = isSearch ? searchCursorHistory : browseCursorHistory;

    if (cursorHistory.length === 0) {
      return;
    }

    const cache = isSearch ? searchCache : browseCache;
    const prevCursor = cursorHistory[cursorHistory.length - 1];
    const cacheKey = prevCursor;

    // キャッシュから取得
    if (cache.has(cacheKey)) {
      setContracts(cache.get(cacheKey)!);
      // 履歴から削除
      if (isSearch) {
        setSearchCursorHistory((prev) => prev.slice(0, -1));
      } else {
        setBrowseCursorHistory((prev) => prev.slice(0, -1));
      }
      setCurrentPage((prev) => Math.max(1, prev - 1)); // Decrement currentPage, ensuring it doesn't go below 1
    }
  }, [
    mode,
    searchCache,
    browseCache,
    searchCursorHistory,
    browseCursorHistory,
  ]);

  // 検索実行
  const searchData = useCallback(
    async (query: string) => {
      setMode("search");
      setSearchQuery(query);
      setSearchCache(new Map()); // 検索キャッシュをクリア
      setSearchCursorHistory([]); // 履歴をクリア
      setCurrentPage(1); // Reset currentPage to 1
      await fetchPaginatedDataInternal(null, query, true, sortOrder);
    },
    [fetchPaginatedDataInternal, sortOrder],
  );

  // 検索クリア
  const clearSearch = useCallback(async () => {
    setMode("browse");
    setSearchQuery("");
    setSearchCache(new Map());
    setSearchCursorHistory([]);
    setCurrentPage(1); // Reset currentPage to 1
    setContracts([]); // Explicitly clear displayed contracts immediately
    // 通常モードの最初のページに戻る
    await fetchPaginatedDataInternal(null, "", false, sortOrder);
  }, [fetchPaginatedDataInternal, setContracts, sortOrder]);

  // ページネーション状態の計算
  const hasNextPage = pagination?.hasMore ?? false;
  const hasPrevPage =
    (mode === "search" ? searchCursorHistory : browseCursorHistory).length > 0;

  return (
    <AllDataContext.Provider
      value={{
        // 既存のプロパティ
        contracts,
        rentRollItems,
        loading,
        error,
        fetchData,
        forceRefresh,
        setContracts,
        // 新しいページネーション機能
        mode,
        dataMode,
        searchQuery,
        pagination,
        hasNextPage,
        hasPrevPage,
        fetchNextPage,
        fetchPrevPage,
        searchData,
        clearSearch,
        goToFirstPage,
        currentPage,
        totalPages,
        itemsPerPage,
        setItemsPerPage,
        sortOrder,
        setSortOrder,
        hasInitialLoaded,
      }}
    >
      {children}
    </AllDataContext.Provider>
  );
};

export const useAllData = () => {
  const context = useContext(AllDataContext);
  if (context === undefined) {
    throw new Error("useAllData must be used within an AllDataProvider");
  }
  return context;
};
